import { useState, useEffect } from "react";
import { useAuth } from "../../context/Auth";
import TopTabBar from "../Tanolet/TopTabBar";
import styles from "./Energy.module.css";

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  AreaChart,
  Area,
} from "recharts";

const mockTrend24h = () =>
  Array.from({ length: 24 }).map((_, i) => ({
    t: `${String(i).padStart(2, "0")}:00`,
    load: 1400 + Math.round(Math.random() * 400),
    thd: +(3 + Math.random() * 3).toFixed(2),
  }));

const StatusBadge = ({ status }) => {
  const cls =
    status === "ok"
      ? styles.badgeOk
      : status === "warn"
        ? styles.badgeWarn
        : styles.badgeCritical;
  return (
    <span className={`${styles.badge} ${cls}`}>{status.toUpperCase()}</span>
  );
};

const KpiCard = ({ label, value, unit, status }) => (
  <div className={styles.kpiCard}>
    <div className={styles.kpiLabel}>{label}</div>
    <div className={styles.kpiBottom}>
      <span className={styles.kpiValue}>
        {value}
        {unit && <span className={styles.kpiUnit}> {unit}</span>}
      </span>
      <StatusBadge status={status} />
    </div>
  </div>
);

const ProgressBar = ({ value }) => (
  <div className={styles.progressBg}>
    <div
      className={`${styles.progressFill} ${value > 85 ? styles.progressWarn : ""}`}
      style={{ width: `${Math.min(value, 100)}%` }}
    />
  </div>
);

const Energy = () => {
  const { mode } = useAuth();
  const [activeTab, setActiveTab] = useState("home");

  const [data, setData] = useState({
    grid: true,
    transformerLoad: 68,
    upsLoad: 62,
    pf: 0.98,
    thd: 4.2,
    freq: 50.01,
    plantLoad: 1820,
    voltage: 415,
    current: 820,
    totalEnergyToday: 48210,
    totalEnergyMonth: 1320000,
  });

  const [alarms, setAlarms] = useState([]);
  const [showAlarmModal, setShowAlarmModal] = useState(false);
  const [trend24h, setTrend24h] = useState([]);
  const [liveTrend, setLiveTrend] = useState([]);

  useEffect(() => {
    setTrend24h(mockTrend24h());

    const id = setInterval(() => {
      const newVoltage = +(410 + Math.random() * 10).toFixed(1);
      const newCurrent = +(780 + Math.random() * 100).toFixed(0);
      const newPF = +(0.94 + Math.random() * 0.05).toFixed(3);
      const newTHD = +Math.max(
        2,
        Math.min(8, 4.2 + (Math.random() - 0.5) * 0.5),
      ).toFixed(1);

      setData((d) => ({
        ...d,
        voltage: newVoltage,
        current: newCurrent,
        pf: newPF,
        thd: newTHD,
        transformerLoad: +Math.max(
          40,
          Math.min(95, d.transformerLoad + (Math.random() - 0.5) * 4),
        ).toFixed(1),
        upsLoad: +Math.max(
          20,
          Math.min(90, d.upsLoad + (Math.random() - 0.5) * 3),
        ).toFixed(1),
        plantLoad: Math.max(
          1200,
          Math.min(2600, d.plantLoad + Math.round((Math.random() - 0.5) * 80)),
        ),
        totalEnergyToday: +(d.totalEnergyToday + Math.random() * 25).toFixed(0),
      }));

      setLiveTrend((prev) => [
        ...prev.slice(-29),
        {
          time: new Date().toLocaleTimeString(),
          load: newCurrent,
          thd: newTHD,
        },
      ]);

      setTrend24h(mockTrend24h());

      if (Math.random() < 0.2) {
        const newAlarm = {
          id: Date.now(),
          time: new Date().toLocaleTimeString(),
          device: ["UPS", "ACB", "AHF", "Transformer"][
            Math.floor(Math.random() * 4)
          ],
          message: [
            "THD High",
            "Overload Warning",
            "Battery Low",
            "Breaker Trip Risk",
          ][Math.floor(Math.random() * 4)],
          priority: Math.random() < 0.5 ? "WARN" : "CRITICAL",
          ack: false,
        };
        setAlarms((a) => [newAlarm, ...a].slice(0, 50));
        setShowAlarmModal(true);
      }
    }, 4000);

    return () => clearInterval(id);
  }, []);

  const acknowledgeAlarm = (id) => {
    setAlarms((prev) =>
      prev.map((a) => (a.id === id ? { ...a, ack: true } : a)),
    );
  };

  const acknowledgeAll = () => {
    setAlarms((prev) => prev.map((a) => ({ ...a, ack: true })));
    setShowAlarmModal(false);
  };

  const unackedAlarms = alarms.filter((a) => !a.ack);
  const firstUnacked = unackedAlarms[0];

  const TABS = [
    { id: "home", label: "Home" },
    { id: "power", label: "Power" },
    { id: "ups", label: "UPS" },
    { id: "acb", label: "ACB" },
    { id: "ahf", label: "AHF" },
    { id: "lines", label: "Lines" },
    { id: "energy", label: "Energy" },
    {
      id: "alarms",
      label: `Alarms${unackedAlarms.length ? ` (${unackedAlarms.length})` : ""}`,
    },
    { id: "trends", label: "Trends" },
  ];

  return (
    <div className={`${styles.root} ${mode ? styles.dark : styles.light}`}>
      <div className={styles.topBar}>
        <TopTabBar text={"Energy"} />
      </div>

      {/* Alarm Banner */}
      {firstUnacked && (
        <div className={styles.alarmBanner}>
          <span className={styles.alarmBannerIcon}>⚡</span>
          <span>
            <b>ALARM:</b> {firstUnacked.device} — {firstUnacked.message}
          </span>
          <button
            className={styles.alarmBannerBtn}
            onClick={() => setShowAlarmModal(true)}
          >
            View
          </button>
        </div>
      )}

      {/* Header */}
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>WALLIKON — PCC Power Dashboard</h1>
          <p className={styles.subtitle}>
            Real-time Power Control Centre Monitoring
          </p>
        </div>
        <span
          className={`${styles.badge} ${data.grid ? styles.badgeOk : styles.badgeCritical}`}
        >
          {data.grid ? "● GRID LIVE" : "● GRID DOWN"}
        </span>
      </div>

      {/* Tabs */}
      <div className={styles.tabBar}>
        {TABS.map((tab) => (
          <button
            key={tab.id}
            className={`${styles.tabBtn} ${activeTab === tab.id ? styles.tabBtnActive : ""}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className={styles.content}>
        {/* HOME */}
        {activeTab === "home" && (
          <div className={styles.tabContent}>
            <div className={styles.kpiGrid}>
              <KpiCard
                label="Transformer Load"
                value={`${(+data.transformerLoad).toFixed(0)}`}
                unit="%"
                status={data.transformerLoad > 85 ? "warn" : "ok"}
              />
              <KpiCard
                label="UPS Load"
                value={`${(+data.upsLoad).toFixed(0)}`}
                unit="%"
                status={data.upsLoad > 80 ? "warn" : "ok"}
              />
              <KpiCard
                label="Plant Load(kW)"
                value={data.plantLoad.toLocaleString()}
                unit=""
                status="ok"
              />
              <KpiCard
                label="THD"
                value={`${(+data.thd).toFixed(1)}`}
                unit="%"
                status={data.thd > 6 ? "warn" : "ok"}
              />
              <KpiCard
                label="Power Factor"
                value={`${(+data.pf).toFixed(3)}`}
                unit=""
                status={data.pf < 0.95 ? "warn" : "ok"}
              />
              <KpiCard
                label="Energy Today(kWh)"
                value={Number(data.totalEnergyToday).toLocaleString()}
                unit=""
                status="ok"
              />
            </div>

            <div className={styles.card}>
              <div className={styles.cardHeader}>Plant Overview</div>
              <div className={styles.overviewGrid}>
                <div className={styles.overviewItem}>
                  <span className={styles.overviewLabel}>Voltage</span>
                  <span className={styles.overviewValue}>{data.voltage} V</span>
                </div>
                <div className={styles.overviewItem}>
                  <span className={styles.overviewLabel}>Current</span>
                  <span className={styles.overviewValue}>{data.current} A</span>
                </div>
                <div className={styles.overviewItem}>
                  <span className={styles.overviewLabel}>Power Factor</span>
                  <span className={styles.overviewValue}>
                    {(+data.pf).toFixed(3)}
                  </span>
                </div>
                <div className={styles.overviewItem}>
                  <span className={styles.overviewLabel}>Frequency</span>
                  <span className={styles.overviewValue}>{data.freq} Hz</span>
                </div>
                <div className={styles.overviewItem}>
                  <span className={styles.overviewLabel}>THD</span>
                  <span className={styles.overviewValue}>
                    {(+data.thd).toFixed(1)}%
                  </span>
                </div>
                <div className={styles.overviewItem}>
                  <span className={styles.overviewLabel}>Status</span>
                  <StatusBadge status="ok" />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* POWER */}
        {activeTab === "power" && (
          <div className={styles.tabContent}>
            <div className={styles.card}>
              <div className={styles.cardHeader}>Transformer</div>
              <div className={styles.cardBody}>
                <div className={styles.metricRow}>
                  <span>Load</span>
                  <span className={styles.metricValue}>
                    {(+data.transformerLoad).toFixed(1)}%
                  </span>
                </div>
                <ProgressBar value={data.transformerLoad} />
                <div className={styles.triGrid}>
                  <div className={styles.statBox}>
                    <div className={styles.statLabel}>Voltage L-L</div>
                    <div className={styles.statVal}>415 V</div>
                  </div>
                  <div className={styles.statBox}>
                    <div className={styles.statLabel}>Current</div>
                    <div className={styles.statVal}>2.3 kA</div>
                  </div>
                  <div className={styles.statBox}>
                    <div className={styles.statLabel}>Temperature</div>
                    <div className={styles.statVal}>72 °C</div>
                  </div>
                </div>
                <div className={styles.triGrid} style={{ marginTop: "1rem" }}>
                  <div className={styles.statBox}>
                    <div className={styles.statLabel}>Voltage (Live)</div>
                    <div className={styles.statVal}>{data.voltage} V</div>
                  </div>
                  <div className={styles.statBox}>
                    <div className={styles.statLabel}>Current (Live)</div>
                    <div className={styles.statVal}>{data.current} A</div>
                  </div>
                  <div className={styles.statBox}>
                    <div className={styles.statLabel}>Power Factor</div>
                    <div className={styles.statVal}>
                      {(+data.pf).toFixed(3)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* UPS */}
        {activeTab === "ups" && (
          <div className={styles.tabContent}>
            <div className={styles.card}>
              <div className={styles.cardHeader}>Galaxy VXL UPS</div>
              <div className={styles.cardBody}>
                <div className={styles.triGrid}>
                  <div className={styles.statBox}>
                    <div className={styles.statLabel}>Mode</div>
                    <div className={styles.statVal}>
                      <StatusBadge status="ok" />
                    </div>
                  </div>
                  <div className={styles.statBox}>
                    <div className={styles.statLabel}>Battery</div>
                    <div className={styles.statVal}>100%</div>
                  </div>
                  <div className={styles.statBox}>
                    <div className={styles.statLabel}>Backup Time</div>
                    <div className={styles.statVal}>18 min</div>
                  </div>
                  <div className={styles.statBox}>
                    <div className={styles.statLabel}>Input</div>
                    <div className={styles.statVal}>OK</div>
                  </div>
                  <div className={styles.statBox}>
                    <div className={styles.statLabel}>Bypass</div>
                    <div className={styles.statVal}>Available</div>
                  </div>
                  <div className={styles.statBox}>
                    <div className={styles.statLabel}>Load</div>
                    <div className={styles.statVal}>
                      {(+data.upsLoad).toFixed(0)}%
                    </div>
                  </div>
                </div>
                <div style={{ marginTop: "1.5rem" }}>
                  <div className={styles.metricRow}>
                    <span>UPS Load</span>
                    <span className={styles.metricValue}>
                      {(+data.upsLoad).toFixed(1)}%
                    </span>
                  </div>
                  <ProgressBar value={data.upsLoad} />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ACB */}
        {activeTab === "acb" && (
          <div className={styles.tabContent}>
            <div className={styles.cardHeader} style={{ marginBottom: "1rem" }}>
              ACB Status
            </div>
            <div className={styles.triGrid}>
              {["INCOMER", "FEEDER-1", "FEEDER-2"].map((b, i) => (
                <div key={i} className={styles.card}>
                  <div className={styles.cardHeader}>{b}</div>
                  <div className={styles.cardBody}>
                    <div className={styles.metricRow}>
                      <span>Status</span>
                      <StatusBadge status="ok" />
                    </div>
                    <div className={styles.metricRow}>
                      <span>Current</span>
                      <span className={styles.metricValue}>
                        {1200 + i * 200} A
                      </span>
                    </div>
                    <div className={styles.metricRow}>
                      <span>Energy</span>
                      <span className={styles.metricValue}>
                        {(3200 + i * 450).toLocaleString()} kWh
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* AHF */}
        {activeTab === "ahf" && (
          <div className={styles.tabContent}>
            <div className={styles.card}>
              <div className={styles.cardHeader}>Active Harmonic Filter</div>
              <div className={styles.cardBody}>
                <div className={styles.triGrid}>
                  <div className={styles.statBox}>
                    <div className={styles.statLabel}>THD Voltage</div>
                    <div className={styles.statVal}>2.8%</div>
                  </div>
                  <div className={styles.statBox}>
                    <div className={styles.statLabel}>THD Current</div>
                    <div className={styles.statVal}>
                      {(+data.thd).toFixed(1)}%
                    </div>
                  </div>
                  <div className={styles.statBox}>
                    <div className={styles.statLabel}>Compensation</div>
                    <div className={styles.statVal}>85%</div>
                  </div>
                  <div className={styles.statBox}>
                    <div className={styles.statLabel}>Status</div>
                    <div className={styles.statVal}>
                      <StatusBadge status="ok" />
                    </div>
                  </div>
                </div>
                <div style={{ marginTop: "1.5rem" }}>
                  <div className={styles.metricRow}>
                    <span>THD Level</span>
                    <span className={styles.metricValue}>
                      {(+data.thd).toFixed(1)}%
                    </span>
                  </div>
                  <ProgressBar value={+data.thd * 12.5} />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* LINES */}
        {activeTab === "lines" && (
          <div className={styles.tabContent}>
            <div className={styles.cardHeader} style={{ marginBottom: "1rem" }}>
              Production Lines
            </div>
            <div className={styles.linesGrid}>
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className={styles.card}>
                  <div className={styles.cardHeader}>LINE {i + 1}</div>
                  <div className={styles.cardBody}>
                    <div className={styles.metricRow}>
                      <span>Load</span>
                      <span className={styles.metricValue}>
                        {120 + i * 10} kW
                      </span>
                    </div>
                    <ProgressBar value={(120 + i * 10) / 2} />
                    <div
                      className={styles.metricRow}
                      style={{ marginTop: "0.75rem" }}
                    >
                      <span>Energy Today</span>
                      <span className={styles.metricValue}>
                        {(2100 + i * 120).toLocaleString()} kWh
                      </span>
                    </div>
                    <div style={{ marginTop: "0.5rem" }}>
                      <StatusBadge status="ok" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ENERGY */}
        {activeTab === "energy" && (
          <div className={styles.tabContent}>
            <div className={styles.kpiGrid}>
              <KpiCard
                label="Energy Today"
                value={Number(data.totalEnergyToday).toLocaleString()}
                unit="kWh"
                status="ok"
              />
              <KpiCard
                label="Energy Month"
                value={Number(data.totalEnergyMonth).toLocaleString()}
                unit="kWh"
                status="ok"
              />
              <KpiCard
                label="Average Power Factor"
                value={(+data.pf).toFixed(3)}
                unit=""
                status={data.pf < 0.95 ? "warn" : "ok"}
              />
            </div>

            <div className={styles.card} style={{ marginTop: "1.5rem" }}>
              <div className={styles.cardHeader}>Energy Breakdown</div>
              <div className={styles.cardBody}>
                <div className={styles.energyBreakdown}>
                  {[
                    { label: "Transformer", value: 1020000, color: "#00c6ff" },
                    { label: "UPS Output", value: 210000, color: "#7b5ea7" },
                    {
                      label: "Production Lines",
                      value: 92000,
                      color: "#f76c6c",
                    },
                    { label: "Utilities", value: 18000, color: "#f8b400" },
                  ].map((item) => (
                    <div key={item.label} className={styles.breakdownItem}>
                      <div className={styles.breakdownLabel}>
                        <span
                          className={styles.breakdownDot}
                          style={{ background: item.color }}
                        />
                        {item.label}
                      </div>
                      <div className={styles.breakdownValue}>
                        {item.value.toLocaleString()} kWh
                      </div>
                      <div className={styles.progressBg}>
                        <div
                          className={styles.progressFill}
                          style={{
                            width: `${(item.value / 1340000) * 100}%`,
                            background: item.color,
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ALARMS */}
        {activeTab === "alarms" && (
          <div className={styles.tabContent}>
            <div className={styles.card}>
              <div className={styles.cardHeader}>
                Alarms
                {unackedAlarms.length > 0 && (
                  <button className={styles.ackAllBtn} onClick={acknowledgeAll}>
                    Acknowledge All
                  </button>
                )}
              </div>
              <div className={styles.cardBody}>
                {alarms.length === 0 ? (
                  <p className={styles.noAlarms}>✓ No alarms</p>
                ) : (
                  <div className={styles.alarmTable}>
                    <div className={styles.alarmTableHead}>
                      <span>Time</span>
                      <span>Device</span>
                      <span>Message</span>
                      <span>Priority</span>
                      <span>Action</span>
                    </div>
                    {alarms.map((a) => (
                      <div
                        key={a.id}
                        className={`${styles.alarmRow} ${a.ack ? styles.alarmRowAck : ""} ${a.priority === "CRITICAL" ? styles.alarmRowCritical : ""}`}
                      >
                        <span className={styles.alarmTime}>{a.time}</span>
                        <span>{a.device}</span>
                        <span>{a.message}</span>
                        <span>
                          <span
                            className={`${styles.badge} ${a.priority === "CRITICAL" ? styles.badgeCritical : styles.badgeWarn}`}
                          >
                            {a.priority}
                          </span>
                        </span>
                        <span>
                          {!a.ack && (
                            <button
                              className={styles.ackBtn}
                              onClick={() => acknowledgeAlarm(a.id)}
                            >
                              ACK
                            </button>
                          )}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* TRENDS */}
        {activeTab === "trends" && (
          <div className={styles.tabContent}>
            <div className={styles.card}>
              <div className={styles.cardHeader}>Load Trend (24h)</div>
              <div className={styles.cardBody}>
                <div className={styles.chartWrap}>
                  <ResponsiveContainer width="100%" height={280}>
                    <AreaChart data={trend24h}>
                      <defs>
                        <linearGradient
                          id="loadGrad"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="5%"
                            stopColor="#00c6ff"
                            stopOpacity={0.3}
                          />
                          <stop
                            offset="95%"
                            stopColor="#00c6ff"
                            stopOpacity={0}
                          />
                        </linearGradient>
                      </defs>
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="rgba(255,255,255,0.07)"
                      />
                      <XAxis
                        dataKey="t"
                        tick={{ fontSize: 11, fill: "#8899aa" }}
                      />
                      <YAxis tick={{ fontSize: 11, fill: "#8899aa" }} />
                      <Tooltip
                        contentStyle={{
                          background: "#1a2230",
                          border: "none",
                          borderRadius: 8,
                          color: "#fff",
                        }}
                      />
                      <Area
                        type="monotone"
                        dataKey="load"
                        stroke="#00c6ff"
                        strokeWidth={2}
                        fill="url(#loadGrad)"
                        dot={false}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            <div className={styles.card} style={{ marginTop: "1.5rem" }}>
              <div className={styles.cardHeader}>THD Trend (24h)</div>
              <div className={styles.cardBody}>
                <div className={styles.chartWrap}>
                  <ResponsiveContainer width="100%" height={220}>
                    <LineChart data={trend24h}>
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="rgba(255,255,255,0.07)"
                      />
                      <XAxis
                        dataKey="t"
                        tick={{ fontSize: 11, fill: "#8899aa" }}
                      />
                      <YAxis tick={{ fontSize: 11, fill: "#8899aa" }} />
                      <Tooltip
                        contentStyle={{
                          background: "#1a2230",
                          border: "none",
                          borderRadius: 8,
                          color: "#fff",
                        }}
                      />
                      <Line
                        type="monotone"
                        dataKey="thd"
                        stroke="#f8b400"
                        strokeWidth={2}
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            <div className={styles.card} style={{ marginTop: "1.5rem" }}>
              <div className={styles.cardHeader}>
                Live Current Trend (last 30 samples)
              </div>
              <div className={styles.cardBody}>
                <div className={styles.chartWrap}>
                  <ResponsiveContainer width="100%" height={220}>
                    <LineChart data={liveTrend}>
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="rgba(255,255,255,0.07)"
                      />
                      <XAxis dataKey="time" hide />
                      <YAxis tick={{ fontSize: 11, fill: "#8899aa" }} />
                      <Tooltip
                        contentStyle={{
                          background: "#1a2230",
                          border: "none",
                          borderRadius: 8,
                          color: "#fff",
                        }}
                      />
                      <Line
                        type="monotone"
                        dataKey="load"
                        stroke="#7b5ea7"
                        strokeWidth={2}
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Alarm Modal */}
      {showAlarmModal && (
        <div
          className={styles.modalOverlay}
          onClick={() => setShowAlarmModal(false)}
        >
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <span>⚡ Active Alarms</span>
              <button
                className={styles.modalClose}
                onClick={() => setShowAlarmModal(false)}
              >
                ✕
              </button>
            </div>
            <div className={styles.modalBody}>
              {unackedAlarms.length === 0 ? (
                <p style={{ color: "#8899aa" }}>No active alarms.</p>
              ) : (
                unackedAlarms.slice(0, 5).map((a) => (
                  <div
                    key={a.id}
                    className={`${styles.modalAlarm} ${a.priority === "CRITICAL" ? styles.modalAlarmCritical : styles.modalAlarmWarn}`}
                  >
                    <div className={styles.modalAlarmTop}>
                      <b>{a.device}</b>
                      <span
                        className={`${styles.badge} ${a.priority === "CRITICAL" ? styles.badgeCritical : styles.badgeWarn}`}
                      >
                        {a.priority}
                      </span>
                    </div>
                    <div>{a.message}</div>
                    <div className={styles.modalAlarmTime}>{a.time}</div>
                  </div>
                ))
              )}
            </div>
            <div className={styles.modalFooter}>
              <button
                className={styles.btnSecondary}
                onClick={() => setShowAlarmModal(false)}
              >
                Close
              </button>
              <button className={styles.btnPrimary} onClick={acknowledgeAll}>
                Acknowledge All
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Energy;
